import { strToU8, zipSync } from "fflate";
import { useFileStore } from "../stores/fileStore";
import { buildBodyExportNames } from "../stores/fileStore/xmlGenerationExportMaps";
import { getSceneState } from "../stores/sceneStore";
import { useRlTrainingSettingsStore } from "../stores/rlTrainingSettingsStore";
import {
  computeMassPropertiesFromCurrentScene,
  type MassPropertiesResult,
  type MassPropertiesRow,
} from "../physics/massPropertiesProbe";
import { sanitizeMjcfName } from "./mjcfNames";
import { buildSceneConfig } from "./environmentBundle";
import { collectResolvedXmlAssetsForZip, resolveMjcfAssetZipPath } from "./environmentZipAssets";
import {
  buildUrdfFromScene,
  type DeploymentConstraintMetadata,
  type DeploymentMeshAssetRef,
  type DeploymentNameMaps,
  type DeploymentWarning,
} from "./deploymentUrdf";
import type {
  Actuator,
  ActuatorUUID,
  Inertia,
  Joint,
  JointUUID,
  MeshUUID,
  Scene,
  UUID,
} from "../types/scene";

type DeploymentBundleBuildResult = {
  filename: string;
  zipBytes: Uint8Array;
  warnings: DeploymentWarning[];
  readiness: DeploymentReadiness;
};

type DeploymentActuatorMapping = {
  actionIndex: number;
  actuatorUuid: ActuatorUUID;
  actuatorName: string;
  urdfActuatorName: string;
  mjcfActuatorName: string;
  jointUuid: JointUUID | null;
  jointName: string | null;
  urdfJointName: string | null;
  mjcfJointName: string | null;
  controlMode: string;
  ctrlrange: [number, number] | null;
  gear: number[] | null;
};

type DeploymentMassPropertiesSidecar = {
  type: "critterapp-mass-properties";
  schemaVersion: 1;
  source: "mujoco_compile";
  computedAtIso: string;
  totalMass: number;
  totalCom: number[];
  bodyCount: number;
  bodies: Array<{
    bodyUuid: string;
    bodyName: string;
    mjcfBodyName: string | null;
    urdfLinkName: string | null;
    mass: number;
    localCom: number[];
    inertialQuat: number[];
    diagInertia: number[];
    worldCom: number[];
    subtreeMass: number;
    subtreeCom: number[];
  }>;
};

export type DeploymentReadiness = {
  status: "ready" | "ready_with_warnings" | "needs_review" | "blocked";
  summary: string;
  counts: {
    warnings: number;
    errors: number;
    actuators: number;
    directJointActuators: number;
    mimicTargetActuators: number;
    unmappedActuators: number;
    urdfMimicJoints: number;
    unsupportedEqualityConstraints: number;
    ballJoints: number;
    collapsedBallJointDofs: number;
    missingMeshWarnings: number;
  };
  blockers: string[];
  warnings: string[];
};

const sanitizeUrdfName = (value: unknown, fallback: string): string => {
  const raw = String(value || fallback || "").trim() || fallback;
  let out = raw.replace(/[^A-Za-z0-9_]/g, "_");
  out = out.replace(/_+/g, "_").replace(/^_+|_+$/g, "");
  if (!out) out = fallback;
  if (!/^[A-Za-z_]/.test(out)) out = `n_${out}`;
  return out;
};

const finiteNumber = (value: unknown, fallback = 0): number => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const formatDeploymentNumber = (value: unknown): string => {
  const n = finiteNumber(value, 0);
  if (Object.is(n, -0)) return "0";
  return Number(n.toPrecision(12)).toString();
};

const formatDeploymentVector = (values: readonly unknown[], length: number): string =>
  Array.from({ length }, (_, index) => formatDeploymentNumber(values[index])).join(" ");

const finiteVector = (values: readonly unknown[] | undefined, fallback: number[]): number[] =>
  fallback.map((fallbackValue, index) => finiteNumber(values?.[index], fallbackValue));

const positiveDiagonal = (values: readonly unknown[] | undefined): [number, number, number] => {
  const diag = finiteVector(values, [1e-9, 1e-9, 1e-9]).map((value) => Math.max(value, 1e-9));
  return [diag[0], diag[1], diag[2]];
};

const isIdentityQuaternion = (values: readonly unknown[]): boolean =>
  finiteNumber(values[0], 1) === 1 &&
  finiteNumber(values[1], 0) === 0 &&
  finiteNumber(values[2], 0) === 0 &&
  finiteNumber(values[3], 0) === 0;

const buildReadableBodyXmlNames = (scene: Scene): Record<string, string> =>
  buildBodyExportNames({ bodiesMap: scene.bodies || {}, xmlNamingMode: "readable" });

const buildBakedInertialFromRow = (row: MassPropertiesRow): Inertia | null => {
  const mass = finiteNumber(row.mass, 0);
  if (!(mass > 0)) return null;
  return {
    uuid: `baked-inertia-${row.bodyUuid}` as UUID,
    name: "MuJoCo computed inertial",
    mass,
    position: finiteVector(row.localCom, [0, 0, 0]) as [number, number, number],
    orientation: finiteVector(row.inertialQuat, [1, 0, 0, 0]) as [number, number, number, number],
    diagonal: positiveDiagonal(row.diagInertia),
  };
};

export function buildSceneWithBakedDeploymentInertials(
  scene: Scene,
  massProperties: MassPropertiesResult
): Scene {
  const inertialByBodyUuid = new Map(
    massProperties.rows
      .map((row) => [row.bodyUuid, buildBakedInertialFromRow(row)] as const)
      .filter(
        (entry): entry is readonly [MassPropertiesRow["bodyUuid"], Inertia] => entry[1] != null
      )
  );
  return {
    ...scene,
    bodies: Object.fromEntries(
      Object.entries(scene.bodies || {}).map(([bodyUuid, body]) => {
        const inertial = inertialByBodyUuid.get(bodyUuid as MassPropertiesRow["bodyUuid"]);
        return [bodyUuid, inertial && body ? { ...body, inertia: inertial } : body];
      })
    ) as Scene["bodies"],
  };
}

export function buildDeploymentMassPropertiesSidecar(args: {
  scene: Scene;
  names: DeploymentNameMaps;
  massProperties: MassPropertiesResult;
}): DeploymentMassPropertiesSidecar {
  const mjcfNames = buildReadableBodyXmlNames(args.scene);
  return {
    type: "critterapp-mass-properties",
    schemaVersion: 1,
    source: "mujoco_compile",
    computedAtIso: new Date(args.massProperties.computedAt).toISOString(),
    totalMass: finiteNumber(args.massProperties.totalMass, 0),
    totalCom: finiteVector(args.massProperties.totalCom, [0, 0, 0]),
    bodyCount: args.massProperties.bodyCount,
    bodies: args.massProperties.rows.map((row) => ({
      bodyUuid: row.bodyUuid,
      bodyName: row.displayName,
      mjcfBodyName: mjcfNames[row.bodyUuid] || row.name || null,
      urdfLinkName: args.names.body[row.bodyUuid] || null,
      mass: finiteNumber(row.mass, 0),
      localCom: finiteVector(row.localCom, [0, 0, 0]),
      inertialQuat: finiteVector(row.inertialQuat, [1, 0, 0, 0]),
      diagInertia: positiveDiagonal(row.diagInertia),
      worldCom: finiteVector(row.worldCom, [0, 0, 0]),
      subtreeMass: finiteNumber(row.subtreeMass, 0),
      subtreeCom: finiteVector(row.subtreeCom, [0, 0, 0]),
    })),
  };
}

export function bakeMassPropertiesIntoMjcfXml(args: {
  xml: string;
  scene: Scene;
  massProperties: MassPropertiesResult;
}): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString(args.xml, "application/xml");
  if (doc.querySelector("parsererror")) {
    throw new Error("Unable to bake inertials into generated MJCF: XML parsing failed.");
  }

  const bodyXmlNameByUuid = buildReadableBodyXmlNames(args.scene);
  const rowByBodyXmlName = new Map<string, MassPropertiesRow>();
  args.massProperties.rows.forEach((row) => {
    const xmlName = bodyXmlNameByUuid[row.bodyUuid];
    if (xmlName) rowByBodyXmlName.set(xmlName, row);
  });

  doc.querySelectorAll("worldbody body").forEach((bodyEl) => {
    const row = rowByBodyXmlName.get(bodyEl.getAttribute("name") || "");
    if (!row) return;
    const inertial = buildBakedInertialFromRow(row);
    if (!inertial) return;

    Array.from(bodyEl.children).forEach((child) => {
      if (child.tagName.toLowerCase() === "inertial") child.remove();
    });

    const inertialEl = doc.createElement("inertial");
    inertialEl.setAttribute("mass", formatDeploymentNumber(inertial.mass));
    inertialEl.setAttribute("pos", formatDeploymentVector(inertial.position, 3));
    if (!isIdentityQuaternion(inertial.orientation)) {
      inertialEl.setAttribute("quat", formatDeploymentVector(inertial.orientation, 4));
    }
    inertialEl.setAttribute("diaginertia", formatDeploymentVector(inertial.diagonal, 3));
    bodyEl.insertBefore(inertialEl, bodyEl.firstChild);
  });

  return new XMLSerializer().serializeToString(doc);
}

function getCompilerMeshdir(xmlDoc: Document): string {
  return String(xmlDoc.querySelector("compiler")?.getAttribute("meshdir") || "assets")
    .trim()
    .replace(/\\/g, "/")
    .replace(/^\/+|\/+$/g, "");
}

function meshUrdfFilenameFromMjcfFile(file: string, compilerMeshdir: string): string {
  return resolveMjcfAssetZipPath(file, compilerMeshdir);
}

function buildSceneMeshUuidToXmlName(scene: Scene): Record<string, string> {
  const usedMeshNames = new Set<string>();
  const meshUuidToXmlName: Record<string, string> = {};
  const reserveUniqueName = (preferred: unknown, fallback: string): string => {
    const base = sanitizeMjcfName(String(preferred || fallback || "").trim() || fallback);
    let candidate = base;
    let suffix = 1;
    while (usedMeshNames.has(candidate)) {
      candidate = `${base}_${suffix}`;
      suffix += 1;
    }
    usedMeshNames.add(candidate);
    return candidate;
  };

  const addMesh = (uuid: string) => {
    if (!uuid || meshUuidToXmlName[uuid]) return;
    const mesh = scene.meshes?.[uuid as MeshUUID];
    const preferredName =
      typeof mesh?.name === "string" && mesh.name.trim()
        ? mesh.name.trim()
        : typeof mesh?.url === "string" && mesh.url.trim()
          ? mesh.url
              .trim()
              .replace(/^.*[\\/]/, "")
              .replace(/\.[^./\\]+$/, "")
          : `mesh_${uuid}`;
    meshUuidToXmlName[uuid] = reserveUniqueName(preferredName, `mesh_${uuid}`);
  };

  Object.values(scene.bodies || {}).forEach((body) => {
    (body.geoms || []).forEach((geom) => {
      if (geom?.mesh) addMesh(String(geom.mesh));
    });
  });

  return meshUuidToXmlName;
}

export function extractMeshAssetRefsFromXml(
  xml: string,
  scene: Scene
): Record<string, DeploymentMeshAssetRef> {
  const refs: Record<string, DeploymentMeshAssetRef> = {};
  const doc = new DOMParser().parseFromString(xml, "application/xml");
  const compilerMeshdir = getCompilerMeshdir(doc);
  const meshAssetsByName: Record<string, DeploymentMeshAssetRef> = {};
  doc.querySelectorAll("asset > mesh").forEach((meshEl) => {
    const name = meshEl.getAttribute("name") || "";
    const file = meshEl.getAttribute("file") || meshEl.getAttribute("obj") || "";
    if (!file) return;
    const scaleAttr = meshEl.getAttribute("scale") || "";
    const scale = scaleAttr
      ? scaleAttr
          .trim()
          .split(/\s+/g)
          .map(Number)
          .filter((value) => Number.isFinite(value))
      : undefined;
    const ref = {
      filename: meshUrdfFilenameFromMjcfFile(file, compilerMeshdir),
      scale: scale && scale.length === 3 ? scale : undefined,
    };
    if (name) meshAssetsByName[name] = ref;
    const uuidMatch = name.match(/^mesh_(.+)$/);
    if (uuidMatch?.[1]) refs[uuidMatch[1]] = ref;
  });

  const meshUuidToXmlName = buildSceneMeshUuidToXmlName(scene);
  Object.entries(meshUuidToXmlName).forEach(([meshUuid, xmlMeshName]) => {
    if (refs[meshUuid]) return;
    const ref = meshAssetsByName[xmlMeshName];
    if (ref) refs[meshUuid] = ref;
  });

  return refs;
}

const extractTimestepFromXml = (xml: string): number | null => {
  const match = xml.match(/<\s*option\b[^>]*\btimestep\s*=\s*"([^"]+)"/i);
  const value = Number(match?.[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
};

const computeFrameSkip = (simDt: number | null, controlHz: number): number => {
  if (!simDt || !Number.isFinite(simDt) || simDt <= 0) return 1;
  const controlDt = 1 / Math.max(1, controlHz);
  return Math.max(1, Math.round(controlDt / simDt));
};

const buildDeploymentReadme = (args: {
  scene: Scene;
  warnings: DeploymentWarning[];
  constraints: DeploymentConstraintMetadata;
  controlHz: number;
  simDt: number | null;
  frameSkip: number;
  actuatorCount: number;
  appVersion: string;
}) => {
  const modelName = args.scene.name || "critter_robot";
  const unsupportedEqualityCount = args.constraints.unsupportedEqualityConstraints.length;
  const ballJointCount = args.constraints.ballJoints.length;
  const collapsedBallJointDofs = args.constraints.ballJoints.reduce(
    (sum, joint) => sum + (Number(joint.collapsedDofs) || 0),
    0
  );
  const mimicCount = args.constraints.urdfMimicJoints.length;
  return `# ${modelName} Deployment Bundle

> Generated by Critter Factory ${args.appVersion} — deployment_config v1 · scene_config v1

This bundle is intended for ROS2/URDF deployment workflows. It keeps the MuJoCo model alongside a URDF approximation and deployment metadata so downstream code can make explicit choices about control, naming, mass properties, and unsupported MuJoCo features.

## Files

- \`robot.urdf\` - URDF robot description for ROS tools, kinematics, visualization, and hardware wiring.
- \`deployment_config.json\` - Deployment metadata: body/joint/actuator naming, action mappings, control timing, limits, mimic joints, unsupported constraints, and warnings.
- \`action_mapping.csv\` - Human-readable policy action to actuator/joint wiring table.
- \`simulate.py\` - Minimal MuJoCo quickstart that loads \`model.xml\`, prints model stats, and runs a passive or headless simulation.
- \`model.xml\` - MuJoCo MJCF export with MuJoCo-computed explicit inertials baked into bodies.
- \`mass_properties.json\` - MuJoCo-computed total and per-body mass/COM/inertia values used for inertial baking.
- \`scene_config.json\` - Critter app scene metadata and editor-facing naming/context.
- \`warnings.json\` - Present when the URDF export dropped or approximated features.
- \`assets/\` - Referenced mesh and texture assets when the source model uses external files.

## Control Summary

- Control Hz: ${args.controlHz}
- MuJoCo timestep: ${args.simDt ?? "unknown"}
- Frame skip: ${args.frameSkip}
- Actuators mapped to policy actions: ${args.actuatorCount}

## Constraint Summary

- URDF mimic joints emitted: ${mimicCount}
- Equality constraints kept as metadata only: ${unsupportedEqualityCount}
- MuJoCo ball joints collapsed in URDF: ${ballJointCount}
- Ball-joint DOFs collapsed in URDF: ${collapsedBallJointDofs}
- Total export warnings: ${args.warnings.length}

Simple linear MuJoCo joint equality constraints may be emitted as URDF \`<mimic>\` joints. General equality constraints, closed-chain constraints, tendon constraints, sensors, and MuJoCo-specific dynamics remain in \`model.xml\` and are summarized in \`deployment_config.json\`.

## Practical Notes

For a quick simulation smoke test, install MuJoCo for Python, unzip the bundle, and run:

\`\`\`bash
pip install mujoco
python simulate.py
python simulate.py --viewer
\`\`\`

Use \`robot.urdf\` for ROS description tooling, but use \`deployment_config.json\` when wiring policy actions to hardware controllers. The policy action order follows MuJoCo actuator order. If a warning references an unsupported feature, do not assume the URDF alone is sufficient for faithful deployment.

Mesh filenames in \`robot.urdf\` are ZIP-relative paths. Some ROS tools, including common RViz2 package workflows, may expect \`package://\` URIs; place the extracted bundle in a ROS package or rewrite those URIs as part of your deployment packaging step.
`;
};

const csvCell = (value: unknown): string => {
  if (value == null) return "";
  const text = Array.isArray(value) ? value.join(" ") : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const buildActionMappingCsv = (args: {
  actuatorMappings: DeploymentActuatorMapping[];
  ros2Control: ReturnType<typeof buildRos2ControlMetadata>;
}) => {
  const ros2ByActuator = new Map(
    (args.ros2Control.jointInterfaces || []).map((entry) => [entry.actuatorUuid, entry])
  );
  const rows = args.actuatorMappings.map((mapping) => {
    const ros2 = ros2ByActuator.get(mapping.actuatorUuid);
    return [
      mapping.actionIndex,
      mapping.actuatorUuid,
      mapping.actuatorName,
      mapping.controlMode,
      mapping.jointUuid || "",
      mapping.jointName || "",
      mapping.urdfJointName || "",
      ros2?.commandInterface || "",
      mapping.ctrlrange || "",
      mapping.gear || "",
      ros2?.hardwareWiringStatus || "",
      ros2?.mimic?.urdfSourceJointName || "",
    ];
  });
  const header = [
    "action_index",
    "actuator_uuid",
    "actuator_name",
    "actuator_kind",
    "joint_uuid",
    "joint_name",
    "urdf_joint_name",
    "command_interface",
    "ctrlrange",
    "gear",
    "hardware_wiring_status",
    "mimic_source_urdf_joint",
  ];
  return [header, ...rows].map((row) => row.map(csvCell).join(",")).join("\n") + "\n";
};

const buildMuJoCoQuickstartScript = () => `#!/usr/bin/env python3
"""
Quick MuJoCo smoke test for this Critter deployment bundle.

Run from the unzipped bundle directory:

    python simulate.py
    python simulate.py --viewer

Dependencies:

    pip install mujoco
"""

from __future__ import annotations

import argparse
import json
from pathlib import Path


def load_config(bundle_dir: Path) -> dict:
    config_path = bundle_dir / "deployment_config.json"
    if not config_path.exists():
        return {}
    with config_path.open("r", encoding="utf-8") as f:
        return json.load(f)


def print_model_summary(model, config: dict) -> None:
    control = config.get("control") or {}
    readiness = config.get("readiness") or {}
    print("Loaded model.xml")
    print(f"  bodies: {model.nbody}")
    print(f"  joints: {model.njnt}")
    print(f"  actuators: {model.nu}")
    print(f"  qpos/qvel: {model.nq}/{model.nv}")
    print(f"  timestep: {model.opt.timestep:g}")
    if control:
        print(f"  bundle control_hz: {control.get('controlHz')}")
        print(f"  bundle frame_skip: {control.get('frameSkip')}")
    if readiness:
        print(f"  readiness: {readiness.get('status')} - {readiness.get('summary')}")


def run_headless(model, data, mujoco, seconds: float) -> None:
    steps = max(1, int(seconds / model.opt.timestep))
    for _ in range(steps):
        mujoco.mj_step(model, data)
    print(f"Stepped simulation for {seconds:g}s ({steps} steps).")
    print(f"  final time: {data.time:g}")


def run_viewer(model, data, mujoco) -> None:
    try:
        from mujoco import viewer
    except Exception as exc:
        raise RuntimeError("mujoco.viewer is unavailable. Try running without --viewer.") from exc

    print("Opening passive MuJoCo viewer. Close the viewer window to exit.")
    with viewer.launch_passive(model, data) as handle:
        while handle.is_running():
            mujoco.mj_step(model, data)
            handle.sync()


def main() -> int:
    parser = argparse.ArgumentParser(description="Run a quick MuJoCo simulation from this deployment bundle.")
    parser.add_argument("--model", default="model.xml", help="Path to the MJCF file inside the bundle.")
    parser.add_argument("--seconds", type=float, default=2.0, help="Headless simulation duration.")
    parser.add_argument("--viewer", action="store_true", help="Open mujoco.viewer.launch_passive instead of headless stepping.")
    args = parser.parse_args()

    bundle_dir = Path(__file__).resolve().parent
    model_path = (bundle_dir / args.model).resolve()
    if not model_path.exists():
        raise FileNotFoundError(f"Model file not found: {model_path}")

    import mujoco

    model = mujoco.MjModel.from_xml_path(str(model_path))
    data = mujoco.MjData(model)
    config = load_config(bundle_dir)
    print_model_summary(model, config)

    expected_actuators = len(((config.get("policyInterface") or {}).get("action") or {}).get("actuatorMappings") or [])
    if expected_actuators and expected_actuators != model.nu:
        print(f"WARNING: deployment_config action mappings={expected_actuators}, MuJoCo actuators={model.nu}")

    if args.viewer:
        run_viewer(model, data, mujoco)
    else:
        run_headless(model, data, mujoco, max(0.0, args.seconds))
    return 0


if __name__ == "__main__":
    raise SystemExit(main())
`;

const getRos2CommandInterface = (actuator: Actuator): "position" | "velocity" | "effort" => {
  const kind = String(actuator.kind || "").toLowerCase();
  if (kind.includes("position")) return "position";
  if (kind.includes("velocity")) return "velocity";
  return "effort";
};

const getJointLimitMetadata = (joint: Joint | null | undefined) => {
  if (!joint) return null;
  const range = joint.kind === "hinge" || joint.kind === "slide" ? joint.range.map(Number) : null;
  const hasRange = range?.length === 2 && range.every((value: number) => Number.isFinite(value));
  return {
    kind: joint.kind || null,
    lower: hasRange ? range[0] : null,
    upper: hasRange ? range[1] : null,
    useLimits: joint.kind === "hinge" || joint.kind === "slide" ? joint.useLimits : null,
    axis:
      joint.kind === "hinge" || joint.kind === "slide"
        ? joint.axis.map((value: unknown) => Number(value) || 0)
        : null,
  };
};

const buildRos2ControlMetadata = (args: {
  scene: Scene;
  names: DeploymentNameMaps;
  constraints: DeploymentConstraintMetadata;
}) => {
  const joints = args.scene.joints || {};
  const mimicByTarget = new Map(
    args.constraints.urdfMimicJoints.map((mimic) => [mimic.targetJointUuid, mimic])
  );
  const actuators = Object.values(args.scene.actuators || {}).filter(Boolean) as Actuator[];
  const jointInterfaces = actuators.map((actuator) => {
    const joint = actuator.joint ? joints[actuator.joint] : null;
    const mimic = actuator.joint ? mimicByTarget.get(actuator.joint) : null;
    const commandInterface = getRos2CommandInterface(actuator);
    return {
      actuatorUuid: actuator.uuid,
      actuatorName: actuator.name,
      actuatorKind: actuator.kind || "general",
      jointUuid: actuator.joint || null,
      jointName: joint?.name || null,
      urdfJointName: joint?.uuid ? args.names.joint[joint.uuid] || null : null,
      commandInterface,
      suggestedCommandInterfaces: [commandInterface],
      suggestedStateInterfaces: ["position", "velocity"],
      ctrlrange: Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange : null,
      forcerange: Array.isArray(actuator.forcerange) ? actuator.forcerange : null,
      gear: Array.isArray(actuator.gear) ? actuator.gear : null,
      jointLimits: getJointLimitMetadata(joint),
      mimic: mimic
        ? {
            sourceJointUuid: mimic.sourceJointUuid,
            sourceJointName: mimic.sourceJointName,
            urdfSourceJointName: mimic.urdfSourceJointName,
            multiplier: mimic.multiplier,
            offset: mimic.offset,
            note: "This joint is represented as a URDF mimic joint. Hardware control should usually command the source joint, not this mimic target.",
          }
        : null,
      hardwareWiringStatus: joint?.uuid
        ? mimic
          ? "mimic_target"
          : "direct_joint"
        : "no_joint_target",
    };
  });

  return {
    schemaVersion: 1,
    note: "This is metadata for ROS2 hardware integration. It is not a complete ros2_control XML file.",
    hardwareComponentName: sanitizeUrdfName(args.scene.name || "critter_robot", "critter_robot"),
    jointInterfaces,
  };
};

const buildDeploymentReadiness = (args: {
  warnings: DeploymentWarning[];
  constraints: DeploymentConstraintMetadata;
  ros2Control: ReturnType<typeof buildRos2ControlMetadata>;
}): DeploymentReadiness => {
  const jointInterfaces = args.ros2Control.jointInterfaces || [];
  const unmappedActuators = jointInterfaces.filter(
    (entry) => entry.hardwareWiringStatus === "no_joint_target"
  ).length;
  const mimicTargetActuators = jointInterfaces.filter(
    (entry) => entry.hardwareWiringStatus === "mimic_target"
  ).length;
  const directJointActuators = jointInterfaces.filter(
    (entry) => entry.hardwareWiringStatus === "direct_joint"
  ).length;
  const missingMeshWarnings = args.warnings.filter(
    (warning) => warning.code === "mesh_path_missing"
  ).length;
  const collapsedBallJointDofs = args.constraints.ballJoints.reduce(
    (sum, joint) => sum + (Number(joint.collapsedDofs) || 0),
    0
  );
  const blockers: string[] = [];
  const reviewWarnings: string[] = [];

  if (unmappedActuators > 0) {
    blockers.push(`${unmappedActuators} actuator(s) do not target a URDF joint.`);
  }
  if (missingMeshWarnings > 0) {
    blockers.push(
      `${missingMeshWarnings} mesh geom(s) could not be resolved into URDF mesh filenames.`
    );
  }
  if (args.constraints.unsupportedEqualityConstraints.length > 0) {
    reviewWarnings.push(
      `${args.constraints.unsupportedEqualityConstraints.length} equality constraint(s) remain metadata-only.`
    );
  }
  if (args.constraints.ballJoints.length > 0) {
    reviewWarnings.push(
      `${args.constraints.ballJoints.length} MuJoCo ball joint(s) are collapsed in the URDF.`
    );
  }
  if (mimicTargetActuators > 0) {
    reviewWarnings.push(
      `${mimicTargetActuators} actuator(s) target URDF mimic joints; hardware should usually command the source joint.`
    );
  }
  if (args.warnings.length > 0) {
    reviewWarnings.push(
      `${args.warnings.length} total export warning(s) are included in warnings.json.`
    );
  }

  const status =
    blockers.length > 0
      ? "needs_review"
      : reviewWarnings.length > 0
        ? "ready_with_warnings"
        : "ready";

  const summary =
    status === "ready"
      ? "Deployment bundle is ready with no export warnings."
      : status === "ready_with_warnings"
        ? "Deployment bundle is usable, but some MuJoCo features are approximated or metadata-only."
        : "Deployment bundle was generated, but hardware wiring needs review before use.";

  return {
    status,
    summary,
    counts: {
      warnings: args.warnings.length,
      errors: 0,
      actuators: jointInterfaces.length,
      directJointActuators,
      mimicTargetActuators,
      unmappedActuators,
      urdfMimicJoints: args.constraints.urdfMimicJoints.length,
      unsupportedEqualityConstraints: args.constraints.unsupportedEqualityConstraints.length,
      ballJoints: args.constraints.ballJoints.length,
      collapsedBallJointDofs,
      missingMeshWarnings,
    },
    blockers,
    warnings: reviewWarnings,
  };
};

const buildDeploymentConfig = (args: {
  scene: Scene;
  names: DeploymentNameMaps;
  warnings: DeploymentWarning[];
  xml: string;
  constraints: DeploymentConstraintMetadata;
}) => {
  const rl = useRlTrainingSettingsStore.getState();
  const controlHz = Math.max(1, Math.floor(Number(rl.controlHz) || 30));
  const simDt = extractTimestepFromXml(args.xml);
  const frameSkip = computeFrameSkip(simDt, controlHz);
  const actuators = Object.values(args.scene.actuators || {}).filter(Boolean) as Actuator[];
  const joints = args.scene.joints || {};
  const ros2Control = buildRos2ControlMetadata({
    scene: args.scene,
    names: args.names,
    constraints: args.constraints,
  });
  const readiness = buildDeploymentReadiness({
    warnings: args.warnings,
    constraints: args.constraints,
    ros2Control,
  });

  const actuatorMappings = actuators.map((actuator, actionIndex) => {
    const joint = actuator.joint ? joints[actuator.joint] : null;
    return {
      actionIndex,
      actuatorUuid: actuator.uuid,
      actuatorName: actuator.name,
      urdfActuatorName:
        args.names.actuator[actuator.uuid] || sanitizeUrdfName(actuator.name, actuator.uuid),
      mjcfActuatorName: sanitizeMjcfName(actuator.name || actuator.uuid),
      jointUuid: actuator.joint || null,
      jointName: joint?.name || null,
      urdfJointName: joint?.uuid ? args.names.joint[joint.uuid] || null : null,
      mjcfJointName: joint ? sanitizeMjcfName(joint.name || joint.uuid) : null,
      controlMode: actuator.kind || "general",
      ctrlrange: Array.isArray(actuator.ctrlrange) ? actuator.ctrlrange : null,
      gear: Array.isArray(actuator.gear) ? actuator.gear : null,
    };
  });

  return {
    type: "critterapp-deployment-config",
    schemaVersion: 1,
    appVersion: (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "0.0.0",
    createdAtIso: new Date().toISOString(),
    target: {
      primary: "ros2-urdf",
      notes:
        "URDF is intended for robot description, visualization, kinematics, and deployment wiring. model.xml remains the MuJoCo-fidelity source and includes explicit MuJoCo-computed inertials for deployment stability.",
    },
    files: {
      readme: "README.md",
      mujocoQuickstart: "simulate.py",
      urdf: "robot.urdf",
      mjcf: "model.xml",
      massProperties: "mass_properties.json",
      sceneConfig: "scene_config.json",
      actionMappingCsv: "action_mapping.csv",
      warnings: args.warnings.length > 0 ? "warnings.json" : null,
    },
    control: {
      controlHz,
      controlDt: 1 / controlHz,
      simDt,
      frameSkip,
      effectiveControlHz: simDt ? 1 / (frameSkip * simDt) : null,
      gripperMode: rl.gripperMode,
    },
    readiness,
    policyInterface: {
      action: {
        kind: rl.gripperMode === "continuous" ? "mujoco_ctrl" : "flat_box_with_binary_gripper",
        actuatorMappings,
      },
      observation: {
        kind: "flat_vector",
        fields: [
          { name: "qpos", source: "mujoco", length: "nq" },
          { name: "qvel", source: "mujoco", length: "nv" },
          { name: "gripperClosed", source: "ctrl_threshold", length: 1 },
        ],
      },
    },
    ros2Control,
    constraints: {
      urdfMimicJoints: args.constraints.urdfMimicJoints,
      unsupportedEqualityConstraints: args.constraints.unsupportedEqualityConstraints,
      ballJoints: args.constraints.ballJoints,
      notes:
        "Simple linear MuJoCo joint equality constraints are emitted as URDF mimic joints. General equality constraints remain in model.xml and are summarized here for controllers, plugins, or hardware-specific deployment code.",
    },
    naming: {
      bodies: Object.values(args.scene.bodies || {}).map((body) => ({
        uuid: body.uuid,
        label: body.name,
        urdfLinkName: args.names.body[body.uuid],
        mjcfBodyName: sanitizeMjcfName(body.uuid || body.name),
      })),
      joints: Object.values(args.scene.joints || {}).map((joint) => ({
        uuid: joint.uuid,
        label: joint.name,
        kind: joint.kind,
        urdfJointName: args.names.joint[joint.uuid],
        mjcfJointName: sanitizeMjcfName(joint.name || joint.uuid),
      })),
      actuators: actuatorMappings,
    },
    warnings: args.warnings,
  };
};

function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function downloadBuiltDeploymentBundle(built: DeploymentBundleBuildResult) {
  const zipBuffer = built.zipBytes.buffer.slice(
    built.zipBytes.byteOffset,
    built.zipBytes.byteOffset + built.zipBytes.byteLength
  ) as ArrayBuffer;
  downloadBlob(built.filename, new Blob([zipBuffer], { type: "application/zip" }));
}

export async function buildDeploymentBundleZip(args?: {
  filenameBase?: string;
}): Promise<DeploymentBundleBuildResult> {
  const filenameBase = args?.filenameBase || "critter_deployment_bundle";
  const fileState = useFileStore.getState();
  const scene = getSceneState() as Scene;
  const xml = await fileState.actions.generateXmlFromState({
    startupPosePolicy: "currentScenePose",
  });
  const massProperties = await computeMassPropertiesFromCurrentScene();
  const bakedScene = buildSceneWithBakedDeploymentInertials(scene, massProperties);
  const bakedXml = bakeMassPropertiesIntoMjcfXml({
    xml,
    scene,
    massProperties,
  });
  const urdfResult = buildUrdfFromScene(bakedScene, {
    modelName: scene.name || "critter_robot",
    meshRefs: extractMeshAssetRefsFromXml(bakedXml, scene),
  });
  const massPropertiesSidecar = buildDeploymentMassPropertiesSidecar({
    scene,
    names: urdfResult.names,
    massProperties,
  });
  const appVersion = (import.meta.env.VITE_APP_VERSION as string | undefined) ?? "0.0.0";
  const sceneConfig = buildSceneConfig();
  const deploymentConfig = buildDeploymentConfig({
    scene,
    names: urdfResult.names,
    warnings: urdfResult.warnings,
    xml: bakedXml,
    constraints: urdfResult.constraints,
  });
  const actionMappingCsv = buildActionMappingCsv({
    actuatorMappings: deploymentConfig.policyInterface.action.actuatorMappings,
    ros2Control: deploymentConfig.ros2Control,
  });
  const rl = useRlTrainingSettingsStore.getState();
  const controlHz = Math.max(1, Math.floor(Number(rl.controlHz) || 30));
  const simDt = extractTimestepFromXml(bakedXml);
  const frameSkip = computeFrameSkip(simDt, controlHz);
  const actuatorCount = Object.values(scene.actuators || {}).filter(Boolean).length;
  const readme = buildDeploymentReadme({
    scene,
    warnings: urdfResult.warnings,
    constraints: urdfResult.constraints,
    controlHz,
    simDt,
    frameSkip,
    actuatorCount,
    appVersion,
  });
  const simulatePy = buildMuJoCoQuickstartScript();

  const zipEntries: Record<string, Uint8Array> = {
    "README.md": strToU8(readme),
    "simulate.py": strToU8(simulatePy),
    "action_mapping.csv": strToU8(actionMappingCsv),
    "robot.urdf": strToU8(urdfResult.urdf),
    "deployment_config.json": strToU8(JSON.stringify(deploymentConfig, null, 2)),
    "model.xml": strToU8(bakedXml),
    "mass_properties.json": strToU8(JSON.stringify(massPropertiesSidecar, null, 2)),
    "scene_config.json": strToU8(JSON.stringify(sceneConfig, null, 2)),
  };
  if (urdfResult.warnings.length > 0) {
    zipEntries["warnings.json"] = strToU8(JSON.stringify(urdfResult.warnings, null, 2));
  }

  const fileMap = fileState.fileMap || {};
  for (const asset of await collectResolvedXmlAssetsForZip(bakedXml, fileMap)) {
    if (!asset.zipPath || asset.zipPath.toLowerCase() === "model.xml") continue;
    zipEntries[asset.zipPath] = new Uint8Array(await asset.file.arrayBuffer());
  }

  return {
    filename: `${filenameBase}.zip`,
    zipBytes: zipSync(zipEntries, { level: 0 }),
    warnings: urdfResult.warnings,
    readiness: deploymentConfig.readiness,
  };
}

export async function downloadDeploymentBundleZip(args?: {
  filenameBase?: string;
}): Promise<DeploymentWarning[]> {
  const built = await buildDeploymentBundleZip(args);
  downloadBuiltDeploymentBundle(built);
  return built.warnings;
}
